import { findLast, isEmpty } from 'lodash'
import { useState, ReactElement, ChangeEvent, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { ClickAwayListener } from '@10xLabs/react-web/components/ClickAwayListener'
import { FormattedTimeByDate } from '@10xLabs/react-web/components/FormattedTimeByDate'
import { StyledLink } from 'components/v2/Link'
import { CopyToClipboard } from '@10xLabs/react-web/components/CopyToClipboard'
import { StopIcon } from '@10xLabs/react-web/components/Icons/Stop'
import { VisibilityIcon } from '@10xLabs/react-web/components/Icons/Visibility'
import { FormattedDate } from '@10xLabs/react-web/components/FormattedDate'
import { Checkbox } from 'components/v2/Checkbox'
import { Dropdown } from 'components/v2/Dropdown'
import List from 'components/v2/List'
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td, TdId, IdWrapper } from 'components/v2/Table'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { TripStatus } from 'features/Trips/components/Status'
import { TripSaleStatus } from 'features/Trips/components/SaleStatus'
import { TripsActions } from 'features/Trips/components/Actions'
import { useLogin } from 'features/Login/hooks'
import { TripsMultipleActions } from 'features/Trips/views/Index/MultipleActions'
import {
  canActivateTripSale,
  canCancelTrip,
  canChangeTripVehicle,
  canChangeCrewUsers,
  canDeactivateTripSale,
  canReadPassengers,
  canWrite,
  isSuperAdmin,
  canCreateSubcampaign,
  canChangeTripDriver,
  canChangeRouteTrip,
  canChangePastTripVehicle,
  canChangeTripConfirmationModalities
} from 'features/Users/utils'
import { SelectedTripsContext } from '..'
import { businessClientsToString } from 'features/Trips/utils'
import { GTTripV2, GTTripV2ItineraryStop } from 'gql/types.generated'
import { DriverCell } from 'features/Trips/components/DriverDetails'
import { TripV2NoteCategory } from 'gql/generated/graphql'

const TripLink = styled.div`
  color: #5d59d4;
  cursor: pointer;
  text-decoration: underline;
`

const StopsWrapper = styled.div`
  display: flex;
  align-items: flex-start;

  > svg {
    margin-right: 8px;
  }
`

const Place = styled.div`
  text-transform: capitalize;
`

const VisibilityButton = styled.button`
  align-items: center;
  border-radius: 5px;
  box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.08);
  border: solid 1px #cacad2;
  background-color: #ffffff;
  color: #5d5c70;
  display: flex;
  justify-content: center;
  padding: 8px;
`

const SeatsNb = styled.span`
  font-size: 14px;
  color: #5d59d7;
`

const HiddenItem = ({ item, canSeeItem }: TSFixMe): ReactElement => {
  const [isShowingItem, setIsShowingItem] = useState(false)

  return (
    <ClickAwayListener onClickAway={(): void => setIsShowingItem(false)}>
      {canSeeItem || isShowingItem ? (
        item
      ) : (
        <VisibilityButton onClick={(): void => setIsShowingItem(true)}>
          <VisibilityIcon />
        </VisibilityButton>
      )}
    </ClickAwayListener>
  )
}

interface ITripsListProps {
  trips: GTTripV2[]
}

export const TripsList = ({ trips = [] }: ITripsListProps): ReactElement => {
  const intl = useIntl()
  const login = useLogin()
  const { selectedTrips, setSelectedTrips, setSelectedTripId } = useContext(SelectedTripsContext)

  const { policies, conditions } = login.accessToken
  const canSeeActions =
    canWrite(FEATURES.TRIP, policies) ||
    canReadPassengers(policies) ||
    canChangeRouteTrip(policies) ||
    canCancelTrip(policies) ||
    canChangeTripVehicle(policies) ||
    canChangePastTripVehicle(policies) ||
    canActivateTripSale(policies) ||
    canDeactivateTripSale(policies) ||
    canChangeCrewUsers(FEATURES.TRIP, policies) ||
    canCreateSubcampaign(policies) ||
    canChangeTripDriver(policies)
  const canSeeAllTickets = isSuperAdmin(conditions)

  const allTripsSelected = selectedTrips.length === trips.length

  const handleAllTripsCheckboxChange = ({ target: { checked } }: ChangeEvent<HTMLInputElement>): void => {
    if (checked) {
      setSelectedTrips(trips)
    } else {
      setSelectedTrips([])
    }
  }

  const shouldShowTripSelection =
    canCancelTrip(policies) ||
    canChangeTripVehicle(policies) ||
    canChangePastTripVehicle(policies) ||
    canChangeTripConfirmationModalities(policies)

  return (
    <TableContainer overflow>
      <Table marginBottom>
        <Thead>
          <Tr>
            {canSeeActions && <Th noUppercase>{shouldShowTripSelection && <TripsMultipleActions />}</Th>}
            {shouldShowTripSelection && (
              <Th>
                <Checkbox id="all-trips-selected" checked={allTripsSelected} onChange={handleAllTripsCheckboxChange} />
              </Th>
            )}
            <Th>
              <FormattedMessage id="dictionary.id" defaultMessage="Id" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.date" defaultMessage="Date" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.status" defaultMessage="Status" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.departure" defaultMessage="Departure" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.realDeparture" defaultMessage="Real departure" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.routeName" defaultMessage="Route Name" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.origin" defaultMessage="Origin" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.destination" defaultMessage="Destination" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.stops" defaultMessage="Stops" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.serviceCode" defaultMessage="Service code" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.tickets" defaultMessage="Tickets" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.vehicle" defaultMessage="Vehicle" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.driver" defaultMessage="Driver" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.line" defaultMessage="Line" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.businessClient" defaultMessage="Business Client" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.sellStatus" defaultMessage="Sell Status" />
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {trips.map((trip, index): ReactElement => {
            const firstStop = trip.itinerary.at(0) as GTTripV2ItineraryStop
            const lastStop = trip.itinerary.at(-1) as GTTripV2ItineraryStop
            const intermediateStops = trip.itinerary.slice(1, -1)
            const isSelectedTrip = selectedTrips.some(({ id }): TSFixMe => id === trip.id)
            const businessClients = businessClientsToString(trip.routeService.businessClients)
            const rsvpNote = findLast(trip.notes || [], ({ category }) => category === TripV2NoteCategory.DriverRsvp)

            const handleTripCheckboxChange = ({ target: { checked } }: ChangeEvent<HTMLInputElement>): void => {
              if (checked) {
                setSelectedTrips([...selectedTrips, trip])
              } else {
                setSelectedTrips(selectedTrips.filter(({ id }): TSFixMe => id !== trip.id))
              }
            }

            return (
              <Tr key={trip.id} isSelected={isSelectedTrip}>
                {canSeeActions && (
                  <Td>
                    <TripsActions trip={trip} selectedTrips={selectedTrips} />
                  </Td>
                )}
                {shouldShowTripSelection && (
                  <Td>
                    <Checkbox
                      id={`trip-${index}-selected`}
                      checked={isSelectedTrip}
                      onChange={handleTripCheckboxChange}
                    />
                  </Td>
                )}
                <TdId>
                  <IdWrapper>
                    <TripLink onClick={(): void => setSelectedTripId(trip.id)}>{trip.id.substring(0, 8)}</TripLink>
                    <CopyToClipboard str={trip.id} />
                  </IdWrapper>
                </TdId>
                <Td>
                  <FormattedDate
                    intl={intl}
                    value={trip.departureAt}
                    month="2-digit"
                    day="2-digit"
                    year="numeric"
                    timeZone={firstStop.place.timezone || undefined}
                  />
                </Td>
                <Td>
                  <TripStatus status={trip.status} />
                </Td>
                <Td>
                  <FormattedTimeByDate
                    intl={intl}
                    date={trip.departureAt}
                    timeZone={firstStop.place.timezone || undefined}
                  />
                </Td>
                <Td>
                  {firstStop.realDepartureTime ? (
                    <FormattedTimeByDate
                      intl={intl}
                      date={firstStop.realDepartureTime}
                      timeZone={firstStop.place.timezone || undefined}
                    />
                  ) : (
                    '-'
                  )}
                </Td>
                <Td>{trip.route.name ?? '-'}</Td>
                <Td>
                  <Place>{firstStop.place.name}</Place>
                </Td>
                <Td>
                  <Place>{lastStop.place.name}</Place>
                </Td>
                <Td>
                  <StopsWrapper>
                    <StopIcon />
                    {intermediateStops.length > 0 ? (
                      <Dropdown toggle={<StyledLink>{intermediateStops.length}</StyledLink>} noPadding>
                        <List.Container>
                          {intermediateStops.map(({ place }): ReactElement => {
                            return (
                              <List.Item key={place.id}>
                                <Place>{place.name}</Place>
                              </List.Item>
                            )
                          })}
                        </List.Container>
                      </Dropdown>
                    ) : (
                      0
                    )}
                  </StopsWrapper>
                </Td>
                <Td>{trip.routeService.code}</Td>
                <Td>
                  <HiddenItem item={trip.confirmedSeatsNumber} canSeeItem={canSeeAllTickets} />
                </Td>
                <Td>
                  <div>
                    <span>{!isEmpty(trip.vehicle) ? trip.vehicle.name : '-'}</span>
                    {!isEmpty(trip.vehicle?.externalID) && (
                      <>
                        &nbsp;
                        <span>({trip.vehicle?.externalID})</span>
                      </>
                    )}
                  </div>
                  {!isEmpty(trip?.vehicle?.id) && (
                    <div>
                      <SeatsNb>
                        <FormattedMessage
                          id="trips.vehicle.capacity"
                          defaultMessage="{capacity} seats"
                          values={{ capacity: trip.availableSeatsNumber }}
                        />
                      </SeatsNb>
                    </div>
                  )}
                </Td>
                <Td>
                  <DriverCell driver={trip.driver || undefined} rsvpNote={rsvpNote} tripId={trip.id} />
                </Td>
                <Td>{trip.companyLine.name}</Td>
                <Td>{isEmpty(trip.routeService.businessClients) ? '-' : businessClients}</Td>
                <Td>
                  <TripSaleStatus saleStatus={trip.saleStatus} />
                </Td>
              </Tr>
            )
          })}
        </Tbody>
      </Table>
    </TableContainer>
  )
}
